import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlarmClockIcon, CheckIcon, FileTextIcon, MailIcon, PhoneIcon, PlusIcon, XCircleIcon } from 'lucide-react';
import { Employee, Lead, LeadSource, LeadStage } from '../../types/pro';
import { LEAD_STAGES, daysSince, kr, toISODate } from '../../utils/pro';
import { PageHeader } from '../../components/pro/PageHeader';
import { SidePanel } from '../../components/pro/SidePanel';
import { LeadWonDialog } from '../../components/pro/LeadWonDialog';
import { NewLeadModal } from '../../components/pro/NewLeadModal';
interface ProLeadsProps {
  leads: Lead[];
  employees: Employee[];
  onUpdate: (id: string, changes: Partial<Lead>) => void;
  onCreate: (lead: Omit<Lead, 'id'>) => void;
  onConvert: (lead: Lead, alsoCreateJob: boolean) => void;
  onMakeQuote: (lead: Lead) => void;
  focusLeadId?: string;
  onClearFocus: () => void;
}
const STALE_DAYS = 7;
export function ProLeads({
  leads,
  employees,
  onUpdate,
  onCreate,
  onConvert,
  onMakeQuote,
  focusLeadId,
  onClearFocus
}: ProLeadsProps) {
  const [openId, setOpenId] = useState<string | null>(focusLeadId ?? null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<LeadStage | null>(null);
  const [wonLead, setWonLead] = useState<Lead | null>(null);
  const [isNewOpen, setIsNewOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const activeId = focusLeadId ?? openId;
  const open = leads.find(lead => lead.id === activeId) ?? null;
  const byStage = useMemo(() => {
    const map = new Map<LeadStage, Lead[]>();
    LEAD_STAGES.forEach(stage => map.set(stage.id, []));
    leads.forEach(lead => map.get(lead.stage)?.push(lead));
    return map;
  }, [leads]);
  const pipelineValue = leads.filter(lead => !['vundet', 'tabt'].includes(lead.stage)).reduce((sum, lead) => sum + lead.estimatedValue, 0);
  const moveTo = (lead: Lead, stage: LeadStage) => {
    if (!lead.id || lead.stage === stage) return;
    if (stage === 'vundet') {
      setWonLead(lead);
      return;
    }
    onUpdate(lead.id, {
      stage,
      lastActivityDate: toISODate(new Date())
    });
  };
  const closePanel = () => {
    setOpenId(null);
    onClearFocus();
  };
  const addNote = () => {
    if (!open?.id || !noteDraft.trim()) return;
    onUpdate(open.id, {
      lastActivityDate: toISODate(new Date()),
      activity: [...open.activity, {
        id: `a-${Date.now()}`,
        date: toISODate(new Date()),
        text: noteDraft.trim(),
        author: 'Dig'
      }]
    });
    setNoteDraft('');
  };
  return <div data-id="element-1131" data-mp="31ydangvfl8s0">
      <PageHeader title="Leads" subtitle={`${leads.filter(l => !['vundet', 'tabt'].includes(l.stage)).length} åbne · ${kr(pipelineValue)} i pipeline`} actions={<button onClick={() => setIsNewOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1133" data-mp="106h2mh8h2h1z">
            <PlusIcon className="h-4 w-4" data-id="element-1134" data-mp="1cnjht2edd7qs" />
            Nyt lead
          </button>} data-id="element-1132" data-mp="31yn278w4jnzt" />

      {/* Kanban — horizontal scroll keeps all seven stages reachable */}
      <div className="-mx-4 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6" data-id="element-1135" data-mp="1me4euleea47l">
        <div className="flex min-w-max gap-3" data-id="element-1136" data-mp="orx7j3qphxrj">
          {LEAD_STAGES.map(stage => {
          const items = byStage.get(stage.id) ?? [];
          const value = items.reduce((sum, l) => sum + l.estimatedValue, 0);
          const isTarget = dragOver === stage.id;
          return <section key={stage.id} onDragOver={e => {
            e.preventDefault();
            setDragOver(stage.id);
          }} onDragLeave={() => setDragOver(null)} onDrop={() => {
            const lead = leads.find(l => l.id === dragId);
            if (lead) moveTo(lead, stage.id);
            setDragId(null);
            setDragOver(null);
          }} className={`flex w-[270px] flex-shrink-0 flex-col rounded-xl border transition-colors ${isTarget ? 'border-brand-400 bg-brand-50/60' : 'border-stone-200 bg-stone-50'}`} data-id="element-1137" data-mp="13p7ga5byspjk">
                <header className="flex items-center gap-2 border-b border-stone-200/70 px-3 py-2.5" data-id="element-1138" data-mp="2vm7j3l089843">
                  <span className={`h-2 w-2 rounded-full ${stage.dot}`} data-id="element-1139" data-mp="2rkcn8w12fmxp" />
                  <h2 className="flex-1 truncate text-sm font-semibold text-stone-800" data-id="element-1140" data-mp="35cisvo6lebiw">
                    {stage.label}
                  </h2>
                  <span className="rounded bg-white px-1.5 py-0.5 text-xs font-semibold tabular-nums text-stone-600" data-id="element-1141" data-mp="3fc19bunanf67">
                    {items.length}
                  </span>
                </header>
                <p className="px-3 pb-2 pt-1.5 text-xs font-medium tabular-nums text-stone-500" data-id="element-1142" data-mp="3tyqfugfyqsm3">
                  {kr(value)}
                </p>

                <ul className="flex-1 space-y-2 p-2 pt-0" data-id="element-1143" data-mp="1tmnwzsx5icq">
                  {items.map(lead => {
                const stale = daysSince(lead.lastActivityDate) > STALE_DAYS && !['vundet', 'tabt'].includes(lead.stage);
                return <li key={lead.id} data-id="element-1144" data-mp="3a9xxy3ij2ty0">
                        <div draggable onDragStart={() => setDragId(lead.id ?? null)} onDragEnd={() => {
                    setDragId(null);
                    setDragOver(null);
                  }} className={`rounded-lg border bg-white p-3 transition-shadow hover:shadow-sm ${stale ? 'border-amber-300' : 'border-stone-200'} ${dragId === lead.id ? 'opacity-50' : ''}`} data-id="element-1145" data-mp="3f1fvsip98eer">
                          <button onClick={() => setOpenId(lead.id ?? null)} className="w-full text-left" data-id="element-1146" data-mp="2d8fx408qyth3">
                            <p className="truncate text-sm font-semibold text-stone-900" data-id="element-1147" data-mp="23gtfnm5tfy5w">
                              {lead.name}
                            </p>
                            <p className="mt-0.5 truncate text-xs text-stone-600" data-id="element-1148" data-mp="2w45ypmcrpnug">
                              {lead.jobType}
                            </p>
                            <p className="mt-2 flex items-baseline justify-between gap-2" data-id="element-1149" data-mp="2m719fdnmlc9f">
                              <span className="truncate text-xs text-stone-400" data-id="element-1150" data-mp="2m8ukr696oknr">
                                {lead.city}
                              </span>
                              <span className="flex-shrink-0 text-sm font-bold tabular-nums text-stone-900" data-id="element-1151" data-mp="1atfns2gz5ssn">
                                {kr(lead.estimatedValue)}
                              </span>
                            </p>
                            <p className="mt-2 flex items-center gap-2 border-t border-stone-100 pt-2" data-id="element-1152" data-mp="3heaaqa56j7en">
                              <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-semibold text-stone-600" data-id="element-1153" data-mp="3reawe2qitzm0">
                                {lead.source}
                              </span>
                              <span className={`ml-auto flex items-center gap-1 text-[11px] ${stale ? 'font-semibold text-amber-600' : 'text-stone-400'}`} data-id="element-1154" data-mp="4yzyhtl84d5m">
                                {stale && <AlarmClockIcon className="h-3 w-3" data-id="element-1155" data-mp="2fr17olms971d" />}
                                {daysSince(lead.createdDate)} dage
                              </span>
                            </p>
                          </button>

                          {/* Phone-friendly stage change, since dragging is awkward on touch */}
                          <label className="mt-2 block lg:hidden" data-id="element-1156" data-mp="lh30l23xwi2y">
                            <span className="sr-only" data-id="element-1157" data-mp="107a9or824oqz">Flyt {lead.name}</span>
                            <select value={lead.stage} onChange={e => moveTo(lead, e.target.value as LeadStage)} className="w-full rounded-md border border-stone-200 bg-stone-50 px-2 py-1.5 text-xs font-medium text-stone-700 outline-none focus:ring-2 focus:ring-brand-500" data-id="element-1158" data-mp="v8gxr38f55sx">
                              {LEAD_STAGES.map(option => <option key={option.id} value={option.id} data-id="element-1159" data-mp="1suxplsydur7a">
                                  {option.label}
                                </option>)}
                            </select>
                          </label>
                        </div>
                      </li>;
              })}

                  {items.length === 0 && <li className="rounded-lg border border-dashed border-stone-200 px-3 py-6 text-center text-xs text-stone-400" data-id="element-1160" data-mp="1mhtleo5h78n5">
                      Tom
                    </li>}
                </ul>
              </section>;
        })}
        </div>
      </div>

      {/* Detail panel */}
      <SidePanel isOpen={Boolean(open)} onClose={closePanel} title={open?.name ?? ''} subtitle={open ? `${open.jobType} · ${open.city}` : undefined} footer={open && <div className="grid grid-cols-2 gap-2" data-id="element-1162" data-mp="2x6ixafz1h32j">
              <button onClick={() => onMakeQuote(open)} className="col-span-2 inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1163" data-mp="2x4pnirt4q2qb">
                <FileTextIcon className="h-4 w-4" data-id="element-1164" data-mp="359zj2fkh622w" />
                Lav tilbud
              </button>
              <button onClick={() => setWonLead(open)} className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700" data-id="element-1165" data-mp="2qd8g5wble88j">
                <CheckIcon className="h-4 w-4" data-id="element-1166" data-mp="2mcydasj3jxxw" />
                Marker som vundet
              </button>
              <button onClick={() => {
        if (open.id) onUpdate(open.id, {
          stage: 'tabt',
          lastActivityDate: toISODate(new Date())
        });
        closePanel();
      }} className="inline-flex items-center justify-center gap-2 rounded-lg border border-stone-200 px-3 py-2.5 text-sm font-semibold text-stone-600 transition-colors hover:bg-stone-50" data-id="element-1167" data-mp="27oblhrca9dps">
                <XCircleIcon className="h-4 w-4" data-id="element-1168" data-mp="1f23tu65m7zsq" />
                Marker som tabt
              </button>
            </div>} data-id="element-1161" data-mp="2j8c8dpc2idf0">
        {open && <div className="space-y-5" data-id="element-1169" data-mp="1o20bvoxi20et">
            <div className="grid grid-cols-2 gap-2" data-id="element-1170" data-mp="1o0z01i8nt9w1">
              <a href={`tel:${open.phone.replace(/\s/g, '')}`} className="inline-flex items-center justify-center gap-2 rounded-lg border border-stone-200 px-3 py-2.5 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1171" data-mp="22z13lsfoxvda">
                <PhoneIcon className="h-4 w-4" data-id="element-1172" data-mp="2bt5ta8j390r8" />
                Ring til kunde
              </a>
              <a href={`mailto:${open.email}`} className="inline-flex items-center justify-center gap-2 rounded-lg border border-stone-200 px-3 py-2.5 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1173" data-mp="27s3qxitvk8l1">
                <MailIcon className="h-4 w-4" data-id="element-1174" data-mp="1eeujr07k4qk4" />
                Send e-mail
              </a>
            </div>

            <dl className="overflow-hidden rounded-xl border border-stone-200" data-id="element-1175" data-mp="1iciikecdf3ha">
              {[['Telefon', open.phone], ['E-mail', open.email], ['Adresse', `${open.address}, ${open.city}`], ['Kilde', open.source], ['Forventet værdi', kr(open.estimatedValue)], ['Oprettet', `${daysSince(open.createdDate)} dage siden`]].map(([label, value]) => <div key={label} className="flex items-baseline gap-3 border-b border-stone-100 px-4 py-2.5 last:border-b-0" data-id="element-1176" data-mp="2658syj4mlepg">
                  <dt className="w-32 flex-shrink-0 text-xs font-medium uppercase tracking-wide text-stone-400" data-id="element-1177" data-mp="bqo9g9s1jp7n">
                    {label}
                  </dt>
                  <dd className="min-w-0 flex-1 text-sm text-stone-900" data-id="element-1178" data-mp="gr2km0oxvf9w">
                    {value}
                  </dd>
                </div>)}
            </dl>

            <div data-id="element-1179" data-mp="4dp33kvfl2i6">
              <h3 className="mb-2 text-sm font-semibold text-stone-900" data-id="element-1180" data-mp="4d6xvyqsjdy4">
                Opgavebeskrivelse
              </h3>
              <p className="rounded-xl border border-stone-200 bg-stone-50 p-4 text-sm leading-relaxed text-stone-700" data-id="element-1181" data-mp="3qmhvlrqc5q21">
                {open.description}
              </p>
            </div>

            <label className="block" data-id="element-1182" data-mp="n5rbya1tzehv">
              <span className="mb-2 block text-sm font-semibold text-stone-900" data-id="element-1183" data-mp="n6t09g7uxxhj">
                Noter
              </span>
              <textarea value={open.notes} onChange={e => open.id && onUpdate(open.id, {
            notes: e.target.value
          })} rows={3} placeholder="Hvad skal huskes om dette lead?" className="w-full rounded-xl border border-stone-200 px-4 py-3 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-1184" data-mp="i4ljmdc31t8v" />
            </label>

            <div data-id="element-1185" data-mp="ve45v5zg99xg">
              <h3 className="mb-2 text-sm font-semibold text-stone-900" data-id="element-1186" data-mp="vene1uzasjya">
                Aktivitet
              </h3>
              <div className="mb-3 flex gap-2" data-id="element-1187" data-mp="gm3e137g2sl2">
                <input value={noteDraft} onChange={e => setNoteDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && addNote()} placeholder="Tilføj hændelse…" className="min-w-0 flex-1 rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-1188" data-mp="lmho0qslwclq" />
                <button onClick={addNote} className="rounded-lg bg-stone-900 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-stone-800" data-id="element-1189" data-mp="14c61t74btcdv">
                  Tilføj
                </button>
              </div>
              <ol className="space-y-3" data-id="element-1190" data-mp="3hjfip56fgd5z">
                {[...open.activity].reverse().map(entry => <li key={entry.id} className="flex gap-3" data-id="element-1191" data-mp="3ckknf3r9ww4z">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-stone-300" data-id="element-1192" data-mp="3rid0zqd9fagj" />
                    <div className="min-w-0 flex-1" data-id="element-1193" data-mp="3mjtfts6hq2p8">
                      <p className="text-sm text-stone-800" data-id="element-1194" data-mp="3mkbjvaze9l7p">{entry.text}</p>
                      <p className="mt-0.5 text-xs text-stone-400" data-id="element-1195" data-mp="2yv0q4dggor32">
                        {entry.author} · {daysSince(entry.date)} dage siden
                      </p>
                    </div>
                  </li>)}
              </ol>
            </div>
          </div>}
      </SidePanel>

      <LeadWonDialog lead={wonLead} onClose={() => setWonLead(null)} onConfirm={choice => {
      if (!wonLead?.id) return;
      if (choice === 'stage-only') {
        onUpdate(wonLead.id, {
          stage: 'vundet',
          lastActivityDate: toISODate(new Date())
        });
      } else {
        onConvert(wonLead, choice === 'customer-and-job');
      }
      setWonLead(null);
      closePanel();
    }} data-id="element-1196" data-mp="33rhen8rkp7go" />

      <NewLeadModal isOpen={isNewOpen} onClose={() => setIsNewOpen(false)} employees={employees} onCreate={onCreate} data-id="element-1197" data-mp="39m5bl0sxs0f7" />
    </div>;
}